import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils/formatters';
import { supabase } from '../../../lib/supabase';
import { checkoutService } from '../../../services/checkoutService';

/**
 * Seller Payment Details & Monthly Statements
 * 
 * Displays monthly accounting statements with aggregated:
 * 1. Number of items purchased for the default monthly period
 * 2. Number of orders
 * 3. Total order values for the seller (without shipping included)
 * 4. Total amount to payout (Total Order Values minus Transaction Fee: percent + flat fee)
 */
const SellerPaymentDetails = ({ sellerId }) => {
  const [statements, setStatements] = useState([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState('');
  const [selectedStatementForModal, setSelectedStatementForModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sellerAccount, setSellerAccount] = useState(null);

  useEffect(() => {
    if (sellerId) {
      loadMonthlyStatements(sellerId);
    } else {
      setLoading(false);
      setError('Seller ID is required to load payment details.');
    }
  }, [sellerId]);

  const loadMonthlyStatements = async (currentSellerId) => {
    try {
      setLoading(true);
      setError('');

      // 1. Resolve Seller Account ID
      let accountId = currentSellerId;
      const { data: accData } = await supabase
        .from('seller_accounts')
        .select('id, user_id, tier, business_name')
        .or(`id.eq.${currentSellerId},user_id.eq.${currentSellerId}`)
        .maybeSingle();

      if (accData?.id) {
        accountId = accData.id;
        setSellerAccount(accData);
      }

      // 2. Fetch platform fee settings (Transaction fee: percent plus Flat fee)
      // Defaults to 5% percentage fee + $0.50 flat fee per order if zero or unconfigured
      let percentFeeRate = 0.05; // 5.0%
      let flatFeeRate = 0.50; // $0.50 per order

      try {
        const { data: feeSetting } = await supabase
          .from('platform_settings')
          .select('value')
          .eq('key', 'fee_configuration')
          .maybeSingle();

        if (feeSetting?.value) {
          const f = feeSetting.value;
          const configuredFlat = parseFloat(f.componentsFlatFee ?? f.modelsFlatFee ?? f.flatFee);
          // Only overwrite if explicitly positive (> 0)
          if (!isNaN(configuredFlat) && configuredFlat > 0) {
            flatFeeRate = configuredFlat;
          }
          const configuredPerc = parseFloat(f.componentsPercentageFee ?? f.modelsPercentageFee ?? f.percentageFee);
          // Only overwrite if explicitly positive (> 0)
          if (!isNaN(configuredPerc) && configuredPerc > 0) {
            percentFeeRate = configuredPerc / 100;
          }
        }
      } catch (fErr) {
        console.warn('Notice loading fee configuration:', fErr);
      }

      // 3. Robustly load orders for this seller via checkoutService (guarantees alignment with SellerOrderList)
      let sellerOrders = [];
      try {
        sellerOrders = await checkoutService.getSellerOrders(currentSellerId);
      } catch (ordErr) {
        console.warn('Notice fetching seller orders via checkoutService:', ordErr);
      }

      // Fallback: If checkoutService returned no orders, query order_items directly
      let directItems = [];
      if (!sellerOrders || sellerOrders.length === 0) {
        const { data: itemsData } = await supabase
          .from('order_items')
          .select(`
            id,
            order_id,
            seller_id,
            quantity,
            unit_price,
            total_price,
            item_status,
            created_at,
            name,
            order:orders (
              id,
              order_number,
              created_at,
              status,
              shipping_amount
            )
          `)
          .or(`seller_id.eq.${accountId},seller_id.eq.${currentSellerId}`)
          .order('created_at', { ascending: false });

        if (itemsData && itemsData.length > 0) {
          directItems = itemsData;
        }
      }

      // 4. Fetch payout records for this seller
      const { data: payoutsData, error: payoutsErr } = await supabase
        .from('payouts')
        .select('*')
        .eq('seller_id', accountId)
        .order('generated_at', { ascending: false });

      if (payoutsErr) {
        console.warn('Notice querying payouts:', payoutsErr.message);
      }

      // 5. Aggregate items into Monthly Statements
      const monthlyMap = {};

      const recordItem = (itemDateRaw, orderId, orderNum, itName, itQty, itPrice, itTotal, itStatus) => {
        const itemDate = new Date(itemDateRaw || new Date());
        const year = itemDate.getFullYear();
        const month = itemDate.getMonth();
        const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;

        if (!monthlyMap[monthKey]) {
          const monthStartDate = new Date(year, month, 1);
          const monthEndDate = new Date(year, month + 1, 0);
          const monthLabel = monthStartDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
          const dateRange = `${monthStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${monthEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

          monthlyMap[monthKey] = {
            key: monthKey,
            label: monthLabel,
            dateRange,
            year,
            month,
            itemsPurchased: 0,
            orderIds: new Set(),
            totalOrderValue: 0,
            transactionFee: 0,
            percentFeeAmount: 0,
            flatFeeAmount: 0,
            payoutAmount: 0,
            status: 'pending',
            items: [],
          };
        }

        const stmt = monthlyMap[monthKey];
        const qty = parseInt(itQty) || 1;
        const lineTotal = parseFloat(itTotal) || ((parseFloat(itPrice) || 0) * qty);

        stmt.itemsPurchased += qty;
        if (orderId || orderNum) {
          stmt.orderIds.add(orderId || orderNum);
        }
        stmt.totalOrderValue += lineTotal;
        stmt.items.push({
          name: itName,
          quantity: qty,
          unit_price: parseFloat(itPrice) || (lineTotal / qty),
          total_price: lineTotal,
          item_status: itStatus,
          order: { order_number: orderNum, id: orderId }
        });
      };

      // Process orders from checkoutService
      if (sellerOrders && sellerOrders.length > 0) {
        sellerOrders.forEach(ord => {
          (ord.items || []).forEach(it => {
            recordItem(
              ord.date || ord.created_at,
              ord.id || ord.orderId,
              ord.order_number || ord.id,
              it.name,
              it.quantity,
              it.price,
              it.total_price || (it.price * it.quantity),
              it.itemStatus
            );
          });
        });
      } else if (directItems && directItems.length > 0) {
        // Process direct items fallback
        directItems.forEach(it => {
          recordItem(
            it.order?.created_at || it.created_at,
            it.order_id || it.order?.id,
            it.order?.order_number || it.order_id,
            it.name,
            it.quantity,
            it.unit_price,
            it.total_price,
            it.item_status
          );
        });
      }

      // Always ensure the current calendar month is present as default period
      const now = new Date();
      const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

      if (!monthlyMap[currentMonthKey]) {
        const curStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const curEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        monthlyMap[currentMonthKey] = {
          key: currentMonthKey,
          label: curStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          dateRange: `${curStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${curEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`,
          year: now.getFullYear(),
          month: now.getMonth(),
          itemsPurchased: 0,
          orderIds: new Set(),
          totalOrderValue: 0,
          transactionFee: 0,
          percentFeeAmount: 0,
          flatFeeAmount: 0,
          payoutAmount: 0,
          status: 'pending',
          items: [],
          isCurrentPeriod: true
        };
      }

      // Convert map to sorted list and calculate Transaction Fee (percent plus Flat fee)
      const statementsList = Object.values(monthlyMap).map(stmt => {
        const orderCount = stmt.orderIds ? stmt.orderIds.size : (stmt.items.length > 0 ? 1 : 0);
        
        // Transaction Fee: sum of the percent fee plus the Flat fee
        let percentFeeAmount = 0;
        let flatFeeAmount = 0;
        let transactionFee = 0;
        let netPayout = 0;

        if (stmt.totalOrderValue > 0) {
          percentFeeAmount = Math.round(stmt.totalOrderValue * percentFeeRate * 100) / 100;
          flatFeeAmount = Math.round(orderCount * flatFeeRate * 100) / 100;
          transactionFee = Math.round((percentFeeAmount + flatFeeAmount) * 100) / 100;
          netPayout = Math.max(0, Math.round((stmt.totalOrderValue - transactionFee) * 100) / 100);
        }

        // Check if a formal payout record exists for this period
        const matchingPayout = (payoutsData || []).find(p => {
          if (!p.period_start) return false;
          const pStart = new Date(p.period_start);
          return pStart.getFullYear() === stmt.year && pStart.getMonth() === stmt.month;
        });

        let payoutStatus = stmt.key === currentMonthKey ? 'Accruing' : 'Ready for Payout';
        let finalPayoutAmount = netPayout;
        let statementId = `STMT-${stmt.key.replace('-', '')}`;

        if (matchingPayout) {
          payoutStatus = matchingPayout.status ? (matchingPayout.status.charAt(0).toUpperCase() + matchingPayout.status.slice(1)) : 'Paid';
          finalPayoutAmount = parseFloat(matchingPayout.amount) || netPayout;
          statementId = matchingPayout.id ? `#${matchingPayout.id.substring(0, 8)}` : statementId;
        }

        return {
          ...stmt,
          statementId,
          orderCount,
          percentFeeRate,
          flatFeeRate,
          percentFeeAmount,
          flatFeeAmount,
          transactionFee,
          payoutAmount: finalPayoutAmount,
          payoutStatus,
          isCurrentPeriod: stmt.key === currentMonthKey
        };
      });

      // Sort statements descending (newest first)
      statementsList.sort((a, b) => b.key.localeCompare(a.key));

      setStatements(statementsList);
      // Default monthly period is the newest/current month
      setSelectedPeriodKey(statementsList[0]?.key || currentMonthKey);

    } catch (err) {
      console.error('Error loading seller monthly statements:', err);
      setError(err?.message || 'Failed to load payment details.');
    } finally {
      setLoading(false);
    }
  };

  // Find currently selected monthly statement
  const activeStatement = statements.find(s => s.key === selectedPeriodKey) || statements[0] || {
    label: 'Current Period',
    dateRange: 'This Month',
    itemsPurchased: 0,
    orderCount: 0,
    totalOrderValue: 0,
    payoutAmount: 0,
    transactionFee: 0,
    payoutStatus: 'Accruing'
  };

  // Overall all-time paid earnings
  const allTimePayouts = statements
    .filter(s => (s.payoutStatus || '').toLowerCase() === 'paid')
    .reduce((sum, s) => sum + s.payoutAmount, 0);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground font-medium text-sm">Calculating monthly statements & payouts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-6">
        <div className="flex items-start gap-3">
          <Icon name="AlertCircle" size={22} className="text-destructive mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-bold text-destructive mb-1">Error Loading Monthly Statements</h3>
            <p className="text-sm text-muted-foreground">{error}</p>
            {sellerId && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadMonthlyStatements(sellerId)}
                className="mt-4"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Retry Calculation
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* 1. Header & Default Monthly Period Selector */}
      <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-border">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-black text-foreground">Monthly Payout Statements</h2>
              {activeStatement?.isCurrentPeriod && (
                <span className="bg-primary/10 text-primary border border-primary/20 text-[10px] font-black uppercase px-2 py-0.5 rounded-full tracking-wider">
                  Default Period
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Aggregated store financial metrics for <span className="font-semibold text-foreground">{activeStatement?.label}</span> ({activeStatement?.dateRange}) — merchandise values only, customer shipping excluded.
            </p>
          </div>

          {/* Period Selector Dropdown */}
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-muted-foreground font-semibold">Select Statement:</span>
            <select
              value={selectedPeriodKey}
              onChange={(e) => setSelectedPeriodKey(e.target.value)}
              className="h-9 px-3 bg-background border border-border rounded-xl text-xs font-bold text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary shadow-sm"
            >
              {statements.map(stmt => (
                <option key={stmt.key} value={stmt.key}>
                  {stmt.label} {stmt.isCurrentPeriod ? '(Current Month)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 2. Key Aggregated Metrics (No Subtexts on Analytics Cards) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3.5 mt-5">
          {/* Metric 1: Items Purchased */}
          <div className="bg-slate-50/80 border border-slate-200/80 rounded-xl p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase font-extrabold text-slate-500 tracking-wider">Items Purchased</span>
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                <Icon name="Package" size={17} />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-black text-foreground tabular-nums">
                {activeStatement.itemsPurchased} <span className="text-sm font-semibold text-muted-foreground">units</span>
              </div>
            </div>
          </div>

          {/* Metric 2: Number of Orders */}
          <div className="bg-slate-50/80 border border-slate-200/80 rounded-xl p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase font-extrabold text-slate-500 tracking-wider">Number of Orders</span>
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600">
                <Icon name="FileText" size={17} />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-black text-foreground tabular-nums">
                {activeStatement.orderCount} <span className="text-sm font-semibold text-muted-foreground">orders</span>
              </div>
            </div>
          </div>

          {/* Metric 3: Total Order Values (Without Shipping) */}
          <div className="bg-slate-50/80 border border-slate-200/80 rounded-xl p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase font-extrabold text-slate-500 tracking-wider">Total Order Values</span>
              <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600">
                <Icon name="Tag" size={17} />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-black text-foreground tabular-nums">
                {formatPrice(activeStatement.totalOrderValue)}
              </div>
            </div>
          </div>

          {/* Metric 4: Transaction Fee (Percent + Flat Fee) */}
          <div className="bg-slate-50/80 border border-slate-200/80 rounded-xl p-4 flex flex-col justify-between">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase font-extrabold text-slate-500 tracking-wider">Transaction Fee</span>
              <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center text-rose-600">
                <Icon name="Calculator" size={17} />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-black text-rose-700 tabular-nums">
                -{formatPrice(activeStatement.transactionFee)}
              </div>
            </div>
          </div>

          {/* Metric 5: Total Amount to Payout */}
          <div className="bg-emerald-50/60 border border-emerald-300 rounded-xl p-4 flex flex-col justify-between shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase font-extrabold text-emerald-800 tracking-wider">Total Amount to Payout</span>
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700">
                <Icon name="CircleDollarSign" size={18} />
              </div>
            </div>
            <div className="mt-3">
              <div className="text-2xl font-black text-emerald-950 tabular-nums">
                {formatPrice(activeStatement.payoutAmount)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Monthly Statements Accounting Table */}
      <div className="bg-card rounded-2xl border border-border p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
          <div>
            <h3 className="text-base font-bold text-foreground">All Monthly Statements</h3>
            <p className="text-xs text-muted-foreground">Historical records and statement breakdowns for your tax & financial reconciliation.</p>
          </div>
          {allTimePayouts > 0 && (
            <div className="text-xs text-muted-foreground font-semibold">
              All-Time Paid: <span className="font-bold text-emerald-700">{formatPrice(allTimePayouts)}</span>
            </div>
          )}
        </div>

        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="min-w-full divide-y divide-border text-left">
            <thead className="bg-muted/70 text-xs font-bold text-muted-foreground uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Statement Period</th>
                <th className="px-4 py-3 text-center">Items Purchased</th>
                <th className="px-4 py-3 text-center">Orders</th>
                <th className="px-4 py-3 text-right">Order Value (Excl. Shipping)</th>
                <th className="px-4 py-3 text-right">Transaction Fee</th>
                <th className="px-4 py-3 text-right">Total Payout Amount</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-sm">
              {statements.map((stmt) => {
                const isSelected = stmt.key === selectedPeriodKey;
                const isPaid = (stmt.payoutStatus || '').toLowerCase() === 'paid';
                const isAccruing = (stmt.payoutStatus || '').toLowerCase() === 'accruing';

                return (
                  <tr 
                    key={stmt.key} 
                    className={`hover:bg-muted/30 transition ${isSelected ? 'bg-primary/5' : ''}`}
                  >
                    {/* Period */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="font-bold text-foreground flex items-center gap-1.5">
                        <span>{stmt.label}</span>
                        {stmt.isCurrentPeriod && (
                          <span className="text-[9px] bg-primary/10 text-primary font-black px-1.5 py-0.5 rounded">
                            CURRENT
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{stmt.dateRange}</div>
                    </td>

                    {/* Items Purchased */}
                    <td className="px-4 py-3.5 whitespace-nowrap text-center font-bold text-foreground tabular-nums">
                      {stmt.itemsPurchased} <span className="text-xs font-normal text-muted-foreground">units</span>
                    </td>

                    {/* Orders */}
                    <td className="px-4 py-3.5 whitespace-nowrap text-center font-bold text-foreground tabular-nums">
                      {stmt.orderCount}
                    </td>

                    {/* Total Order Values (Excl. Shipping) */}
                    <td className="px-4 py-3.5 whitespace-nowrap text-right font-semibold text-foreground tabular-nums">
                      {formatPrice(stmt.totalOrderValue)}
                    </td>

                    {/* Transaction Fee (Percent + Flat Fee) */}
                    <td className="px-4 py-3.5 whitespace-nowrap text-right text-xs text-muted-foreground tabular-nums">
                      -{formatPrice(stmt.transactionFee)}
                    </td>

                    {/* Total Amount to Payout */}
                    <td className="px-4 py-3.5 whitespace-nowrap text-right font-black text-emerald-800 tabular-nums">
                      {formatPrice(stmt.payoutAmount)}
                    </td>

                    {/* Status Badge */}
                    <td className="px-4 py-3.5 whitespace-nowrap text-center">
                      <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full border ${
                        isPaid 
                          ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                          : isAccruing
                          ? 'bg-amber-100 text-amber-800 border-amber-300'
                          : 'bg-blue-100 text-blue-800 border-blue-300'
                      }`}>
                        {stmt.payoutStatus}
                      </span>
                    </td>

                    {/* Action */}
                    <td className="px-4 py-3.5 whitespace-nowrap text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedStatementForModal(stmt)}
                        className="text-xs h-8 px-2.5"
                      >
                        <Icon name="Eye" size={13} className="mr-1" />
                        Details
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. Statement Itemized Details Modal */}
      {selectedStatementForModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex justify-between items-start pb-4 border-b border-gray-200 shrink-0">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Icon name="FileSpreadsheet" size={20} className="text-primary" />
                  Monthly Statement Breakdown — {selectedStatementForModal.label}
                </h3>
                <p className="text-xs text-gray-500 mt-1 font-mono">
                  {selectedStatementForModal.dateRange} • Statement {selectedStatementForModal.statementId}
                </p>
              </div>
              <button
                onClick={() => setSelectedStatementForModal(null)}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-lg transition"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Modal Summary KPI Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 my-4 p-3 bg-slate-50 border border-slate-200 rounded-xl shrink-0 text-center">
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Items Purchased</span>
                <span className="text-base font-black text-slate-800">{selectedStatementForModal.itemsPurchased} units</span>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Orders</span>
                <span className="text-base font-black text-slate-800">{selectedStatementForModal.orderCount}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-slate-400 block">Order Value (Excl. Shipping)</span>
                <span className="text-base font-black text-slate-800">{formatPrice(selectedStatementForModal.totalOrderValue)}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-rose-700 block">Transaction Fee</span>
                <span className="text-base font-black text-rose-800">-{formatPrice(selectedStatementForModal.transactionFee)}</span>
              </div>
              <div>
                <span className="text-[10px] uppercase font-bold text-emerald-700 block">Payout Amount</span>
                <span className="text-base font-black text-emerald-800">{formatPrice(selectedStatementForModal.payoutAmount)}</span>
              </div>
            </div>

            {/* Itemized Table */}
            <div className="flex-1 overflow-y-auto pr-1">
              <h4 className="text-xs font-black uppercase tracking-wider text-slate-500 mb-2">Itemized Purchased Goods</h4>
              {selectedStatementForModal.items?.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200">
                  <Icon name="Package" size={32} className="text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-500">No items purchased in this statement period yet.</p>
                </div>
              ) : (
                <div className="rounded-xl border border-gray-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 text-left text-xs">
                    <thead className="bg-gray-50 text-[10px] uppercase font-bold text-gray-500">
                      <tr>
                        <th className="px-3 py-2.5">Item Name</th>
                        <th className="px-3 py-2.5 text-center">Quantity</th>
                        <th className="px-3 py-2.5 text-right">Unit Price</th>
                        <th className="px-3 py-2.5 text-right">Line Total (Excl. Shipping)</th>
                        <th className="px-3 py-2.5 text-right">Transaction Fee</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700">
                      {selectedStatementForModal.items.map((item, iIdx) => {
                        const qty = parseInt(item.quantity) || 1;
                        const lineTotal = parseFloat(item.total_price) || ((parseFloat(item.unit_price) || 0) * qty);
                        
                        // Transaction Fee for this item: proportional share of the statement's total transaction fee (percentage + flat fee)
                        const itemShare = selectedStatementForModal.totalOrderValue > 0 
                          ? (lineTotal / selectedStatementForModal.totalOrderValue) 
                          : (1 / (selectedStatementForModal.items.length || 1));
                        const itemFee = Math.round(selectedStatementForModal.transactionFee * itemShare * 100) / 100;
                        const name = item.name || item.seller_products?.products?.name || item.seller_components?.components?.name || 'Item';

                        return (
                          <tr key={iIdx} className="hover:bg-slate-50">
                            <td className="px-3 py-2.5 font-medium">
                              <div>{name}</div>
                              {item.order?.order_number && (
                                <div className="text-[10px] text-gray-400 font-mono">Order #{item.order.order_number}</div>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-center font-bold">{qty}</td>
                            <td className="px-3 py-2.5 text-right tabular-nums">{formatPrice(item.unit_price || (lineTotal / qty))}</td>
                            <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatPrice(lineTotal)}</td>
                            <td className="px-3 py-2.5 text-right text-xs text-rose-700 font-medium tabular-nums">-{formatPrice(itemFee)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50/80 font-bold border-t border-gray-200 text-xs text-gray-800">
                      <tr>
                        <td colSpan={3} className="px-3 py-2.5 text-right uppercase tracking-wider text-[10px] text-gray-500">Totals:</td>
                        <td className="px-3 py-2.5 text-right font-black text-gray-900">{formatPrice(selectedStatementForModal.totalOrderValue)}</td>
                        <td className="px-3 py-2.5 text-right font-black text-rose-700 tabular-nums">-{formatPrice(selectedStatementForModal.transactionFee)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="pt-4 border-t border-gray-200 mt-4 flex items-center justify-between shrink-0">
              <span className="text-xs text-gray-500 font-medium">
                Status: <strong className="text-gray-900">{selectedStatementForModal.payoutStatus}</strong>
              </span>
              <Button variant="default" onClick={() => setSelectedStatementForModal(null)}>
                Close Statement
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerPaymentDetails;
